<script lang="ts" setup>
import { createEditor, $getRoot, $createParagraphNode, $createTextNode } from 'lexical'
import type { LexicalEditor } from 'lexical'
import { registerPlainText } from '@lexical/plain-text'
import { registerHistory, createEmptyHistoryState } from '@lexical/history'

const t2 = useT2Base({
  Optional: 'Pasirinktinai',
  Message: 'Žinutė',
})
export type Props = {
  nolabel?: boolean
  label: stringI18n | None
  placeholder?: stringI18n
  value: string | None
  optional?: boolean
  classEditor?: string | (string | undefined)[]
  classPlaceholder?: string | (string | undefined)[]
  classLabel?: string | (string | undefined)[]
  max?: number
  error?: string
  focused?(i: boolean): void
}

const props = defineProps<Props>()
const cleanup = useCleanup()
const elem = useTemplateRef('elem')

const data = shallowReactive({
  editor: undefined as undefined | LexicalEditor,
  size: helpers.toString(props.value).length,
})

function editorInit(elem: HTMLElement): LexicalEditor {
  if (data.editor) return data.editor
  const editor = createEditor({
    editable: true,
    namespace: 'text editor',
    theme: {},
  })
  data.editor = editor
  editor.setRootElement(elem)
  cleanup(
    () => editor?.setRootElement(null),
    registerPlainText(editor),
    registerHistory(editor, createEmptyHistoryState(), 300),
    editor.registerTextContentListener((text) => (data.size = text.length))
  )
  return editor
}

function setState(editor: LexicalEditor, text: string) {
  data.size = text.length
  editor.update(() => {
    const root = $getRoot()
    if (!root.isEmpty()) root.clear()
    root.append($createParagraphNode().append($createTextNode(text)))
  })
}

watch(
  () => ({ value: props.value, el: elem.value }),
  ({ value, el }) => el && setState(editorInit(el), value || ''),
  { immediate: true }
)

const myInput = useMyInput<string>({
  label: () => ts(props.label),
  value: () => '',
  empty: () => false,
  input: () => data.editor?.getEditorState().read(() => $getRoot().getTextContent()) ?? '',
  optional: () => props.optional,
})

defineExpose({
  ...myInput.expose(),
  reset() {
    if (data.editor) setState(data.editor, props.value || '')
  },
})
</script>

<template>
  <div class="_relative _w100p">
    <div v-if="label && !nolabel" :class="[$style.top, classLabel]">
      <h4 v-if="label" class="noline">{{ label }}</h4>
      <span v-if="optional" class="noline _italic">({{ t2('Optional') }})</span>
      <UiMsg2 :err="error" />
      <span v-if="max" class="noline _italic _mla _pr08">{{ data.size }}/{{ max }}</span>
    </div>

    <div :class="[$style.ph, classEditor, classPlaceholder, data.size && 'off']">{{ placeholder ?? t2('Message') }}</div>

    <div ref="elem" v-memo="[classEditor]" contenteditable="true" :class="[$style.editor, classEditor]" @focus.passive="focused?.(true)" @blur.passive="focused?.(false)" />

    <slot />
  </div>
</template>

<style module>
.font {
  composes: global(_lh16);
  composes: global(_fs1);
  composes: global(_fw-l);

  color: var(--blue2);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Arial', sans-serif;
}
.box {
  padding-top: 24px;
  composes: global(_pb2);
  composes: global(_w100p);
}
.editor {
  composes: global(_hm34);
  composes: box;
  composes: font;

  outline: none;
}
.ph {
  composes: global(_absolute);
  composes: global(_h100p);
  composes: global(_noselect);
  composes: global(_c4);

  composes: font;
  composes: box;
  opacity: 0.5;
}
.ph:global(.off) {
  visibility: hidden;
}
.top {
  composes: global(_absolute);
  composes: global(_row);
  composes: global(_gap06);
  composes: global(_h20);
  composes: global(_w100p);
  composes: global(_noselect);

  top: 0;
  left: 0;
}
</style>
